import React, { useEffect, useState } from "react"
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Modal, Row } from "react-bootstrap"
import PropTypes from "prop-types"
import axios from "axios"
import getServerURL from "../../../../../functions/getServerURL"
import { handleErrorFetchingData } from "../../../../../functions/errorHandling"
import LoadingAnimation from "../../../../../reuseable-components/LoadingAnimation"
import UserMultiSelect from "./UserMultiSelect"
import swal from "sweetalert"

import ExportFunctionalGroup from "../ExportFunctionalGroup"
import DeleteFunctionalGroupModal from "../DeleteFunctionalGroupModal"

export default function CreateEditFunctionalGroupModal({ getFunctionalGroups, mode = "create", functionalGroup = null }) {
  // STATE
  const [show, toggleShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState(functionalGroup ? functionalGroup.name : "")
  const [genesisUsers, setGenesisUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [assignedUsers, setAssignedUsers] = useState(functionalGroup ? functionalGroup.users : [])
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([])
  const [errorMessage, setErrorMessage] = useState("")
  const [originalUsers] = useState(functionalGroup ? functionalGroup.users : [])
  const [reason, setReason] = useState("")
  const [reasonError, setReasonError] = useState("")

  const handleCancel = () => {
    toggleShow(false)
    setLoading(false)
    setName(functionalGroup ? functionalGroup.name : "")
    setGenesisUsers([])
    setAssignedUsers(functionalGroup ? functionalGroup.users : [])
    setSelectedUsers([])
    setSelectedAssignedUsers([])
    setErrorMessage("")
    setReason("")
    setReasonError("")
  }

  const handleSubmit = () => {
    setErrorMessage("")
    setReasonError("")
    
    if (!name.trim()) {
      setErrorMessage("Group name cannot be empty.")
      return
    }
    if (name.trim().length < 3) {
      setErrorMessage("Group name must be at least 3 characters.")
      return
    }

    // For create mode, use the old endpoint
    if (mode === "create") {
      const functionalGroupObject = { name, users: assignedUsers.map(user => user.id) }

      swal({
        text: `Creating the functional group... Please stand by.`,
        icon: require("../../../../../static/images/spinner.gif"),
        button: false
      })

      axios.post(`${getServerURL()}functional-groups/`, functionalGroupObject, {
        headers: { "Authorization": `Bearer ${ localStorage.getItem("token") }` }
      }).then(response => {

        swal({
          title: "Success",
          text: `Functional group ${name} has been created`,
          icon: "success",
          button: "Continue",
        }).then(() => {
          getFunctionalGroups()
          handleCancel()
        })
      }).catch(error => {
        const message = `There was an error creating the functional group`
        handleErrorFetchingData(error, message)
      })
      return
    }

    // For update mode, check if users or name have changed
    const originalUserIds = originalUsers.map(u => u.id).sort()
    const currentUserIds = assignedUsers.map(u => u.id).sort()
    const usersChanged = JSON.stringify(originalUserIds) !== JSON.stringify(currentUserIds)
    const nameChanged = name !== (functionalGroup ? functionalGroup.name : "")

    if (!nameChanged && !usersChanged) {
      setErrorMessage("No changes to save.")
      return
    }
    if (usersChanged && (!reason || reason.trim().length < 10)) {
      setReasonError("Reason must be at least 10 characters long")
      return
    }
    submitUpdate(usersChanged ? reason.trim() : null, nameChanged, usersChanged)
  }

  const submitUpdate = (reason = null, nameChanged = false, usersChanged = false) => {
    swal({
      text: `Updating the functional group... Please stand by.`,
      icon: require("../../../../../static/images/spinner.gif"),
      button: false
    })

    const payload = {};
    if (nameChanged) {
      payload.name = name;
    }
    if (usersChanged) {
      payload.users = assignedUsers.map(user => user.id);
      payload.reason = reason;
    }

    axios.patch(`${getServerURL()}functional-groups/${functionalGroup.id}/`, payload, {
      headers: { "Authorization": `Bearer ${ localStorage.getItem("token") }` }
    }).then(response => {

      swal({
        title: "Success",
        text: `Functional group ${name} has been updated`,
        icon: "success",
        button: "Continue",
      }).then(() => {
        getFunctionalGroups()
        handleCancel()
      })
    }).catch(error => {
      const message = `There was an error updating the functional group`
      handleErrorFetchingData(error, message)
    })
  }

  const transferSelectedUsers = (multiselectMode = "add") => {
    if (multiselectMode === "add") {
      let updatedAssignedUserList = [...assignedUsers, ...selectedUsers].sort((a, b) => a.first_name.localeCompare(b.first_name))
      let updatedGenesisUserList = [...genesisUsers].filter(({ id: id1 }) => {
        return !selectedUsers.some(({ id: id2 }) => id2 === id1)
      })
      setSelectedUsers([])
      setGenesisUsers(updatedGenesisUserList)
      setAssignedUsers(updatedAssignedUserList)
    } else {
      let updatedUserList = [...genesisUsers, ...selectedAssignedUsers].sort((a, b) => a.first_name.localeCompare(b.first_name))
      let updatedAssignedUserList = [...assignedUsers].filter(({ id: id1 }) => {
        return !selectedAssignedUsers.some(({ id: id2 }) => id2 === id1)
      })
      setSelectedAssignedUsers([])
      setAssignedUsers(updatedAssignedUserList)
      setGenesisUsers(updatedUserList)
    }
  }

  const selectAllUsers = (multiselectMode, searchTerm = "") => {
    let lowerCaseSearchTerm = searchTerm.toLowerCase();
    if (multiselectMode === "add") {
      let updatedGenesisUsers = genesisUsers.filter(option => {
        const full_name = `${option.first_name} ${option.last_name}`.toLowerCase();
        return full_name.includes(lowerCaseSearchTerm);
      })
      setSelectedUsers(updatedGenesisUsers)
    } else {
      let updatedAssignedUsers = assignedUsers.filter(option => {
        const full_name = `${option.first_name} ${option.last_name}`.toLowerCase();
        return full_name.includes(lowerCaseSearchTerm);
      })
      setSelectedAssignedUsers(updatedAssignedUsers)
    }
  }

  const handleGetGenesisUsers = () => {
    setLoading(true)
    axios.get(`${getServerURL()}users/get_genesis_users/`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
    }).then(response => {
      let genesisUserData = response.data
      if (functionalGroup) {
        genesisUserData = genesisUserData.filter(({ id: id1 }) => {
          return !functionalGroup.users.some(({ id: id2 }) => id2 === id1)
        })
      }
      setGenesisUsers(genesisUserData)
    }).catch(error => {
      handleErrorFetchingData(error)
    }).finally(() => {
      setLoading(false)
    })
  }

  useEffect(() => {
    if (show) {
      handleGetGenesisUsers()
    }
  }, [show])

  return (
    <React.Fragment>

      <br />
      <Button onClick={ () => toggleShow(true) }>{ mode === "create" ? "New" : "Edit" } Functional Group</Button>
      {mode === "update" && (
        <div>
        <DeleteFunctionalGroupModal
          functionalGroup={functionalGroup}
          getFunctionalGroups={getFunctionalGroups}
        />
        </div>
      )}
      {mode === "create" ?
       null
       :
       <div>
       <br />
       <ExportFunctionalGroup
       functionalGroup={functionalGroup}
       />
       </div>
      }
      <Modal show={ show } onHide={ handleCancel }>
        <Modal.Header closeButton>
          <Modal.Title>{ mode === "create" ? "New" : "Edit" } Functional Group</Modal.Title>
        </Modal.Header>
        <Modal.Body className="new-training-form">
          { loading ? <LoadingAnimation />
            :
            <React.Fragment>
              <div>
                <h5>Name: <span className="required">*</span></h5>
                <FormControl
                  id="formControlProjectTitle"
                  value={ name }
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a name"
                />
              </div>
              <div>
                <Row>
                  <Col xs={6} sm={6} md={6} lg={6}>
                    <h5>Users: </h5>
                    <UserMultiSelect
                      onChange={ setSelectedUsers }
                      users={ genesisUsers }
                      setSelectedGroupsFunction={ setSelectedUsers }
                      setOppositeGroupsFunction={ setAssignedUsers }
                      selectedUsers={ selectedUsers }
                      transferSelectedUsers={ transferSelectedUsers }
                      selectAllUsers={ selectAllUsers }
                    />
                  </Col>
                  <Col xs={6} sm={6} md={6} lg={6}>
                    <h5>Selected Users: </h5>
                    <UserMultiSelect
                      onChange={ setSelectedAssignedUsers }
                      users={ assignedUsers }
                      mode="remove"
                      setSelectedGroupsFunction={ setSelectedAssignedUsers }
                      setOppositeGroupsFunction={ setGenesisUsers }
                      selectedUsers={ selectedAssignedUsers }
                      transferSelectedUsers={ transferSelectedUsers }
                      selectAllUsers={ selectAllUsers }
                    />
                  </Col>
                </Row>
              </div>
              
              {/* Show reason field only when users have been modified in update mode */}
              {mode === "update" && (() => {
                const originalUserIds = originalUsers.map(u => u.id).sort()
                const currentUserIds = assignedUsers.map(u => u.id).sort()
                const usersChanged = JSON.stringify(originalUserIds) !== JSON.stringify(currentUserIds)
                
                if (usersChanged) {
                  return (
                    <div style={{ marginTop: '20px' }}>
                      <FormGroup controlId="formControlsReason" validationState={reasonError ? "error" : null}>
                        <ControlLabel>
                          Reason for Change <span style={{ color: 'red' }}>*</span>
                        </ControlLabel>
                        <FormControl
                          componentClass="textarea"
                          rows={3}
                          placeholder="Please provide a reason for this change (minimum 10 characters)"
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value)
                            if (reasonError && e.target.value.trim().length >= 10) {
                              setReasonError("")
                            }
                          }}
                          style={{ resize: 'vertical' }}
                        />
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginTop: '5px',
                          fontSize: '12px'
                        }}>
                          <span style={{ color: reasonError ? '#a94442' : '#999' }}>
                            {reasonError || `${reason.trim().length} / 10 characters minimum`}
                          </span>
                          <span style={{ color: '#999' }}>
                            {reason.length} characters
                          </span>
                        </div>
                      </FormGroup>
                    </div>
                  )
                }
                return null
              })()}
              
              { errorMessage &&
                <React.Fragment>
                  <br/>
                  <Alert bsStyle="danger">{ errorMessage }</Alert>
                </React.Fragment>
              }
            </React.Fragment>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={ handleCancel }>Cancel</Button>
          <Button onClick={ handleSubmit } bsStyle="primary" disabled={loading}>Submit</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}


CreateEditFunctionalGroupModal.propTypes = {
  mode: PropTypes.oneOf(["create", "update"]),
  functionalGroup: PropTypes.object,
  getFunctionalGroups: PropTypes.func.isRequired
}



// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/FunctionalGroupContainer/CreateEditFunctionalGroupModal/index.js